import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/shared/utils/cn';

import { Input } from './Input';
import { Label } from './Label';
import { SelectMenuOptionLeading } from './SelectMenuOptionLeading';

export interface SelectMenuOption {
  value: string;
  label: string;
  /** Secondary line in the menu (e.g. status or slug). */
  description?: string;
  /** When true, shows an avatar slot (image or initials) on the left. */
  showAvatar?: boolean;
  imageUrl?: string;
  imageAlt?: string;
  /** Extra text included in search matching. */
  searchText?: string;
}

export interface SelectMenuProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectMenuOption[];
  /** Shown when `value` is empty or not found in `options`. */
  placeholder: string;
  disabled?: boolean;
  /** When true, a first row clears selection (`value` → `''`), separated from the list. */
  allowClear?: boolean;
  /** Label for the clear row (defaults to `placeholder`). */
  clearLabel?: string;
  /** Shown when `options` is empty. */
  emptyLabel: string;
  /**
   * When true, shows a filter field at the top of the menu. Pass `searchPlaceholder` and
   * `noResultsLabel` for copy; `noResultsLabel` is shown when the query matches nothing.
   */
  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Shown when `showSearch` is on, `options` is non-empty, and the query matches no option. */
  noResultsLabel?: string;
  className?: string;
  /** Passed to the menu content; use for wider panels when labels are long. */
  contentClassName?: string;
}

function normalizeSearch(s: string) {
  return s.trim().toLowerCase();
}

function optionMatchesQuery(option: SelectMenuOption, query: string) {
  if (query === '') return true;
  const haystack = [option.label, option.value, option.description, option.searchText]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function SelectMenuCheckIndicator({ selected }: { selected: boolean }) {
  if (!selected) {
    return <span className="h-5 w-5 shrink-0" aria-hidden="true" />;
  }
  return (
    <span
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary"
      aria-hidden="true"
    >
      <Check className="h-3 w-3 text-fg-on-primary" strokeWidth={3} />
    </span>
  );
}

function SelectMenuItemRow({
  option,
  selected,
  onSelect,
  muted,
}: {
  option:
    | SelectMenuOption
    | { value: string; label: string; description?: string; showAvatar?: boolean };
  selected: boolean;
  onSelect: () => void;
  muted?: boolean;
}) {
  const hasDescription = Boolean(option.description);
  const showAvatar = 'showAvatar' in option && option.showAvatar;

  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2.5 outline-none transition-colors',
        'data-[highlighted]:bg-primary-subtle',
        selected && 'bg-primary-subtle',
      )}
    >
      {showAvatar ? (
        <SelectMenuOptionLeading
          label={option.label}
          imageUrl={'imageUrl' in option ? option.imageUrl : undefined}
          imageAlt={'imageAlt' in option ? option.imageAlt : undefined}
        />
      ) : null}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm',
            hasDescription || showAvatar ? 'font-semibold' : 'font-medium',
            muted && 'text-fg-muted',
            !muted && selected && 'text-primary',
            !muted && !selected && 'text-fg',
          )}
        >
          {option.label}
        </span>
        {hasDescription ? (
          <span className="block truncate text-xs text-fg-muted">{option.description}</span>
        ) : null}
      </span>
      <SelectMenuCheckIndicator selected={selected} />
    </DropdownMenu.Item>
  );
}

export function SelectMenu({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  allowClear,
  clearLabel,
  emptyLabel,
  showSearch,
  searchPlaceholder,
  noResultsLabel,
  className,
  contentClassName,
}: SelectMenuProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showSearch) {
      searchInputRef.current?.focus();
    }
  }, [showSearch]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find((o) => o.value === value) ?? null;
  }, [options, value]);

  const normalizedQuery = normalizeSearch(searchQuery);

  const filteredOptions = useMemo(() => {
    if (!showSearch || normalizedQuery === '') return options;
    return options.filter((o) => optionMatchesQuery(o, normalizedQuery));
  }, [options, showSearch, normalizedQuery]);

  const isEmpty = options.length === 0;
  const showNoResults =
    Boolean(showSearch) && !isEmpty && normalizedQuery !== '' && filteredOptions.length === 0;

  const handleOpenChange = (open: boolean) => {
    if (!open) setSearchQuery('');
  };

  return (
    <DropdownMenu.Root onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            'group flex h-9 w-full items-center justify-between gap-3 rounded-sm border border-border-strong bg-surface px-3 text-left text-sm shadow-sm',
            'transition-colors hover:border-primary/40 hover:bg-surface-muted/50',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
            'disabled:cursor-not-allowed disabled:opacity-60',
            className,
          )}
        >
          {selectedOption ? (
            <span className="flex min-w-0 flex-1 items-center gap-3">
              {selectedOption.showAvatar ? (
                <SelectMenuOptionLeading
                  label={selectedOption.label}
                  imageUrl={selectedOption.imageUrl}
                  imageAlt={selectedOption.imageAlt}
                />
              ) : null}
              <span className="min-w-0 flex-1 truncate font-medium text-fg">
                {selectedOption.label}
              </span>
            </span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-fg-subtle">{placeholder}</span>
          )}
          <ChevronDown
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-fg-subtle transition-transform duration-200 group-data-[state=open]:rotate-180"
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="start"
          className={cn(
            'z-50 flex max-h-[min(22rem,calc(100vh-8rem))] min-w-[var(--radix-dropdown-menu-trigger-width)] flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
            contentClassName,
          )}
        >
          {showSearch ? (
            <div
              className="sticky top-0 z-10 shrink-0 border-b border-border bg-surface p-3"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle"
                />
                <Input
                  ref={searchInputRef}
                  type="search"
                  autoComplete="off"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder={searchPlaceholder ?? ''}
                  aria-label={searchPlaceholder ?? placeholder}
                  className="h-9 rounded-md border-border-strong py-2 pl-9 pr-3 text-sm"
                />
              </div>
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 [scrollbar-width:thin]">
            {isEmpty ? (
              <div className="px-3 py-2.5 text-sm text-fg-muted">{emptyLabel}</div>
            ) : (
              <>
                {allowClear ? (
                  <>
                    <SelectMenuItemRow
                      option={{ value: '', label: clearLabel ?? placeholder }}
                      selected={value === ''}
                      onSelect={() => onChange('')}
                      muted
                    />
                    <DropdownMenu.Separator className="my-1 h-px bg-border" />
                  </>
                ) : null}
                {showNoResults ? (
                  <div className="px-3 py-2.5 text-sm text-fg-muted">
                    {noResultsLabel ?? emptyLabel}
                  </div>
                ) : (
                  filteredOptions.map((opt) => (
                    <SelectMenuItemRow
                      key={opt.value}
                      option={opt}
                      selected={value === opt.value}
                      onSelect={() => onChange(opt.value)}
                    />
                  ))
                )}
              </>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export interface SelectMenuFieldProps extends SelectMenuProps {
  label: string;
}

/** Label + {@link SelectMenu} with the same spacing as form fields on create / edit screens. */
export function SelectMenuField({ label, id, ...selectProps }: SelectMenuFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {label}
      </Label>
      <SelectMenu id={id} {...selectProps} />
    </div>
  );
}
